import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

interface HorizontalBase64ImageProps {
    src: string;
    alt?: string;
    className?: string;
}

const rotateBase64 = async (src: string): Promise<string> => {
    const img = new Image();
    img.decoding = 'async';
    img.src = src;

    await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Image failed to load'));
    });

    if (img.naturalWidth >= img.naturalHeight) {
        return src;
    }

    const canvas = document.createElement('canvas');

    canvas.width = img.naturalHeight;
    canvas.height = img.naturalWidth;

    const ctx = canvas.getContext('2d');

    if (!ctx) {
        return src;
    }

    // move origin to right side, rotate 90deg clockwise, draw
    ctx.translate(canvas.width, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(img, 0, 0);

    // keep same mime type if possible (png/jpeg), fallback png
    const mime = src.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,/)?.[1] ?? 'image/png';

    return canvas.toDataURL(mime, 1);
};

const HorizontalBase64Image = ({ src, alt, className }: HorizontalBase64ImageProps) => {
    const [fixedSrc, setFixedSrc] = useState(src);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const out = await rotateBase64(src);
                if (!cancelled) setFixedSrc(out);
            } catch {
                if (!cancelled) setFixedSrc(src);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [src]);

    return <img className={classNames('justify-self-center w-full max-w-sm h-full object-contain rounded', className)} src={fixedSrc} alt={alt} />;
};

export default HorizontalBase64Image;
